import * as React from 'react';
import classNames from 'classnames';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EDTableCell from '../../../../../../../BaseComponents/Table/Components/EDTableCell';
import { IFormatter } from '../../../../../../../Utils/Formatters/FormatterTypes';
import EDIcon from '../../../../../../../BaseComponents/Icon/EDIcon';
import EDTooltip from '../../../../../../../BaseComponents/Tooltip/EDTooltip';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';

interface IProps {
  value?: string;
  variant?: 'head';
  subTableHeader?: boolean;
  textVariant?: 'subtitle2' | 'body2';
  selected?: boolean;
  rowSelected?: boolean;
  showExpandMore?: boolean;
  showExpandLess?: boolean;
  pointer?: boolean;
  alignment?: Alignment;
  toolTip?: string;
  formatter?: IFormatter;
  formatterParams?: string;
  paddingLeft?: number;
  paddingRight?: number;
  showDetailsIcon?: boolean;
  tooltipText?: string;
  children?: React.ReactNode;
  onClick?: () => void;
  onMouseOver?: (
    e: React.MouseEvent<HTMLTableCellElement | HTMLTableHeaderCellElement>
  ) => void;
  stickyTop?: number;
  noVerticalSpacing?: boolean;
  style?: React.CSSProperties;
  colSpan?: number;
  disablePointer?: boolean;
  id?: string;
  disableNoWrap?: boolean;
  clickableObjectColorMarking?: boolean;
  hovering?: boolean;
  extraBold?: boolean;
  classes?: Partial<Record<keyof ReturnType<typeof styles>, string>>;
  className?: string;
}

type Alignment = 'left' | 'right' | 'space-between';

const styles = (theme: Theme) =>
  ({
    contentContainer: {
      display: 'flex',
      flex: '0 1 auto',
      alignItems: 'center',
      position: 'relative',
      zIndex: 90,
    },
    headerCell: {
      verticalAlign: 'bottom',
      padding: '16px',
      position: 'sticky',
      zIndex: 120,
      backgroundColor: theme.palette.common.white,
    },
    subTableHeaderCell: {
      zIndex: 50,
    },
    cell: {
      '&&': {
        padding: '4px 12px',
        paddingRight: '10px',
        height: '30px',
        lineHeight: 1.25,
      },
    },
    noVerticalSpacing: {
      '&&': {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
      },
    },
    contentContainerJustifyLeft: {
      justifyContent: 'flex-start',
      textAlign: 'start',
    },
    contentContainerJustifyRight: {
      justifyContent: 'flex-end',
      textAlign: 'right',
    },
    contentContainerJustifySpaceBetween: {
      justifyContent: 'space-between',
    },
    selectedColumnBG: {
      backgroundColor:
        theme.customProperties.EkonomiskAnalysRowColors.selectedColumnBGColor,
    },
    selectedRowFG: {
      color: theme.customProperties.EkonomiskAnalysRowColors.selectedRowFGColor,
    },
    selectedRowPointer: {
      cursor: 'pointer',
    },
    clickableObject: {
      color: theme.palette.primary.main,
      '&:hover': {
        borderBottom: '2px solid ' + theme.palette.primary.main,
        marginBottom: -2,
      },
    },
    headerHover: {
      borderBottom: '2px solid ' + theme.palette.primary.main,
      marginBottom: -2,
    },
    extraBold: {
      fontWeight: 700,
    },
  } as const);

const EkonomiskAnalysTableCell = (props: IProps) => {
  const {
    alignment = 'right',
    pointer,
    paddingLeft = 16,
    onClick,
    onMouseOver,
    selected,
    showExpandMore,
    showExpandLess,
    showDetailsIcon,
    tooltipText = '',
    value = '',
    textVariant,
    rowSelected,
    formatter,
    formatterParams = '',
    variant,
    children,
    stickyTop,
    noVerticalSpacing,
    id,
    disableNoWrap,
    clickableObjectColorMarking,
  } = props;
  const classes = withStyles.getClasses(props);
  const cellPaddingLeft = paddingLeft;

  const alignmentClass =
    alignment === 'left'
      ? classes.contentContainerJustifyLeft
      : alignment === 'right'
      ? classes.contentContainerJustifyRight
      : classes.contentContainerJustifySpaceBetween;

  return (
    <EDTooltip title={tooltipText}>
      <EDTableCell
        variant={variant}
        onClick={onClick}
        onMouseOver={onMouseOver}
        className={classNames(classes.cell, props.className, {
          [`${classes.selectedColumnBG}`]: selected,
          [`${classes.headerCell}`]: variant === 'head',
          [classes.subTableHeaderCell]: props.subTableHeader,
          [`${classes.noVerticalSpacing}`]: noVerticalSpacing,
        })}
        style={{
          cursor: pointer ? 'pointer' : 'default',
          paddingLeft: cellPaddingLeft,
          top: stickyTop ? stickyTop : 0,
        }}
        colSpan={props.colSpan}
        id={id}
      >
        <span className={classNames(classes.contentContainer, alignmentClass)}>
          <React.Fragment>
            {showExpandMore && (
              <EDIcon iconType="expand_more" marginRight={1} />
            )}
            {showExpandLess && (
              <EDIcon iconType="expand_less" marginRight={1} />
            )}
          </React.Fragment>
          {showDetailsIcon && (
            <EDIcon iconType="expand_details" color="white" />
          )}
          {!children && (
            <EDTypography
              className={classNames({
                [`${classes.selectedRowFG}`]: rowSelected,
                [classes.selectedRowPointer]:
                  rowSelected && !props.disablePointer,
                [classes.clickableObject]: clickableObjectColorMarking,
                [classes.headerHover]: props.hovering,
                [classes.extraBold]: props.extraBold,
              })}
              variant={textVariant}
              style={{
                whiteSpace: disableNoWrap ? 'normal' : 'nowrap',
              }}
            >
              {formatter &&
                formatter.displayer.display(value, true, formatterParams)}
              {!formatter && value}
            </EDTypography>
          )}
          {children && children}
        </span>
        <span className="colPaint" />
      </EDTableCell>
    </EDTooltip>
  );
};

export default withStyles(EkonomiskAnalysTableCell, styles);
